// ─────────────────────────────────────────────────────────────────────────────
// GENERATED FILE — DO NOT EDIT.
//
// Copied from shared/branding/BrandingProvider.jsx by shared/sync-branding.js.
// Edit the original and re-run the sync; changes made here are overwritten and
// will silently un-share this component from the other NexusAI apps.
// ─────────────────────────────────────────────────────────────────────────────

import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";

/**
 * BrandingProvider — decides what this page is called and what colour it is,
 * from the hostname it was served under.
 *
 * WHY THIS EXISTS
 *
 * Every NexusAI frontend compiled its own logo and name into the bundle. That
 * makes one build serve exactly one brand, so `academy.abc.com` and
 * `nexusaiweb.com` could never look different however the DNS was arranged.
 * This turns branding into data fetched at boot.
 *
 * THE FIRST PAINT MUST NOT FLASH
 *
 * Branding arrives over the network, and a login screen that shows NexusAI for
 * 300ms before turning into the partner's brand is worse than not white-labelling
 * at all — it tells every visitor exactly whose platform they are really on.
 *
 * Two things prevent it. The last known branding for this hostname is cached in
 * localStorage and applied SYNCHRONOUSLY before React renders, so a returning
 * visitor never sees the wrong brand. A first-time visitor gets `ready: false`,
 * and apps hold the shell back until it flips — a blank moment is recoverable,
 * a wrong logo is not.
 *
 * FAILING MEANS FALLING BACK, NEVER BLOCKING
 *
 * If the endpoint is down, unreachable or slow, the platform brand renders and
 * the app carries on. Branding is decoration; nothing here may stop someone
 * logging in.
 */

// Must match tenant_branding.js PLATFORM_DEFAULT on the server. Duplicated
// deliberately: this has to be usable before any network call has completed,
// including when that call fails.
export const PLATFORM_BRANDING = Object.freeze({
  mode: "hosted",
  display_name: "NexusAI",
  short_name: "NexusAI",
  logo_url: "",
  logo_dark_url: "",
  favicon_url: "",
  colors: { primary: "#4f46e5", accent: "#0ea5e9" },
  support_email: "",
  support_url: "",
  show_nexusai_logo: false,
  show_powered_by: false,
  certificate_attribution: "full",
  is_platform: true,
});

const CACHE_PREFIX = "nexusai:branding:";
const BrandingContext = createContext({ branding: PLATFORM_BRANDING, ready: true, error: null });

const currentHost = () =>
  (typeof window !== "undefined" && window.location && window.location.hostname) || "";

/** Read the cached brand for THIS hostname, synchronously. */
function readCache(host) {
  try {
    const raw = window.localStorage.getItem(CACHE_PREFIX + host);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Keyed by hostname, so a laptop that visited two partner domains never
    // shows one partner's brand on the other's site.
    return parsed && parsed.host === host ? parsed.branding : null;
  } catch {
    return null;
  }
}

function writeCache(host, branding) {
  try {
    window.localStorage.setItem(CACHE_PREFIX + host, JSON.stringify({ host, branding, at: Date.now() }));
  } catch {
    // Private browsing, or a full quota. Not worth surfacing — the only cost is
    // a flash on the next visit.
  }
}

/**
 * Apply branding to the document itself.
 *
 * The tab title, the favicon and the CSS variables live outside React's tree,
 * so they are set imperatively. Colours become custom properties rather than
 * inline styles so that a stylesheet can consume them without every component
 * subscribing to context.
 */
function applyToDocument(b) {
  if (typeof document === "undefined") return;

  const root = document.documentElement;
  // The server validates these as hex before they are ever stored, so they
  // cannot carry a CSS payload — but they are still only ever assigned to a
  // custom property, never interpolated into a rule.
  if (b.colors && b.colors.primary) root.style.setProperty("--brand-primary", b.colors.primary);
  if (b.colors && b.colors.accent) root.style.setProperty("--brand-accent", b.colors.accent);

  if (b.display_name) {
    // Preserve whatever the app put after a dash, so "Dashboard · NexusAI"
    // becomes "Dashboard · Acme Academy" rather than losing the page name.
    const current = document.title || "";
    const parts = current.split(/\s[·|–—-]\s/);
    document.title = parts.length > 1
      ? `${parts.slice(0, -1).join(" · ")} · ${b.display_name}`
      : b.display_name;
  }

  if (b.favicon_url) {
    let link = document.querySelector('link[rel="icon"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = b.favicon_url;
  }
}

export function BrandingProvider({ children, apiBase, host: hostOverride }) {
  const host = hostOverride || currentHost();

  // Read the cache during the first render, not in an effect. An effect runs
  // after paint, which is the flash this exists to prevent.
  const cached = useRef(null);
  if (cached.current === null) cached.current = readCache(host) || false;

  const [branding, setBranding] = useState(cached.current || PLATFORM_BRANDING);
  // A cached brand is good enough to render immediately; a cold visitor waits.
  const [ready, setReady] = useState(Boolean(cached.current));
  const [error, setError] = useState(null);

  // Apply the cached values before the first paint.
  const applied = useRef(false);
  if (!applied.current && cached.current) {
    applied.current = true;
    applyToDocument(cached.current);
  }

  useEffect(() => {
    let cancelled = false;
    const base = (apiBase || "").replace(/\/$/, "");

    // Even a cached visitor re-fetches, so a logo change reaches them on the
    // next load rather than never.
    (async () => {
      try {
        const res = await fetch(`${base}/public/branding`, {
          headers: { Accept: "application/json" },
          credentials: "omit",
        });
        if (!res.ok) throw new Error(`branding ${res.status}`);
        const body = await res.json();
        const b = (body && (body.res || body.data || body)) || PLATFORM_BRANDING;
        if (cancelled) return;
        setBranding(b);
        applyToDocument(b);
        writeCache(host, b);
        setError(null);
      } catch (e) {
        if (cancelled) return;
        // Fall back rather than block. Nothing here may stop someone logging in.
        setError(e);
        if (!cached.current) applyToDocument(PLATFORM_BRANDING);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();

    return () => { cancelled = true; };
  }, [apiBase, host]);

  const value = useMemo(() => ({ branding, ready, error }), [branding, ready, error]);
  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding() {
  return useContext(BrandingContext);
}

/**
 * The mark in a header.
 *
 * Renders the partner's logo where there is one, and their NAME where there is
 * not — never a broken image and never an empty space. A partner who has not
 * uploaded a logo yet should still see their own name, not ours.
 */
export function BrandLogo({ className = "", dark = false, fallbackClassName = "" }) {
  const { branding } = useBranding();
  const src = dark ? (branding.logo_dark_url || branding.logo_url) : branding.logo_url;

  if (!src) {
    return (
      <span className={fallbackClassName || className} data-testid="brand-name">
        {branding.display_name}
      </span>
    );
  }
  return (
    <img
      src={src}
      alt={branding.display_name}
      className={className}
      data-testid="brand-logo"
      // A logo that 404s must not leave a broken-image icon in the header.
      onError={(e) => { e.currentTarget.style.display = "none"; }}
    />
  );
}

/**
 * The footer credit.
 *
 * Renders only in `standalone`. The decision is the SERVER's — the mode is
 * projected into `show_powered_by` there, so a client cannot decide to hide it
 * by passing a prop, and four apps cannot drift apart on when it appears.
 */
export function PoweredBy({ className = "" }) {
  const { branding } = useBranding();
  if (!branding.show_powered_by) return null;
  return (
    <span className={className} data-testid="powered-by">
      Powered by <a href="https://nexusaiweb.com" target="_blank" rel="noopener noreferrer">NexusAI</a>
    </span>
  );
}

/** The NexusAI mark shown beside a partner's in `cobranded`. */
export function CoBrandMark({ className = "", src = "/nexusai-logo.svg" }) {
  const { branding } = useBranding();
  if (!branding.show_nexusai_logo) return null;
  return <img src={src} alt="NexusAI" className={className} data-testid="cobrand-mark" />;
}
